import FleetTitle from '@/libs/antd/components/Title';
import { FC, Fragment, useState } from 'react';
import { useFRMPraSurveyInput } from './PraSurveyInputPage';
import {
	Alert,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import {
	// EditOutlined,
	LoadingOutlined,
	FileSearchOutlined,
	UploadOutlined,
	EditOutlined,
} from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { CustomerLegalDocument } from '@/libs/fleet-api/services/types';
import FleetModal from '@/libs/antd/components/Modal';
import rule from '@/libs/antd/rules';
import dayjs from 'dayjs';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';

type FRMPraSurveyInputReferralDocumentProps = {
	valid?: boolean;
	customerId?: string;
};
type ReferralDocumentItem = CustomerLegalDocument<'payload'> &
	Pick<CustomerLegalDocument, 'documentType' | 'isMandatory' | 'file'>;

type ReferralDocumentForm = {
	input?: ReferralDocumentItem;
	items: ReferralDocumentItem[];
};

export const useFRMPraSurveyInputReferralDocumentForm = () => {
	return useForm<ReferralDocumentForm>();
};

const FRMPraSurveyInputReferralDocument: FC<
	FRMPraSurveyInputReferralDocumentProps
> = ({ valid = false }) => {
	const { referralDocumentForm, customerReferralDocuments, mainInformation } =
		useFRMPraSurveyInput();

	const { form, fieldName } = referralDocumentForm;
	const [activeIndexModal, setActiveIndexModal] = useState<number>();
	// const { navigate } = useApp();
	const { openPreviewModal } = useModals();

	const handleSubmit = ({ input }: ReferralDocumentForm) => {
		const items = form.getFieldValue(
			fieldName('items'),
		) as ReferralDocumentItem[];
		form.setFieldsValue({
			items: items.map((item, index) => {
				if (index === activeIndexModal) return input;
				return item;
			}),
		} as ReferralDocumentForm);
		setActiveIndexModal(undefined);
		form.resetFields([fieldName(['input'])]);
	};

	const getInitialValues = () => {
		return {
			items:
				customerReferralDocuments?.map((data) => {
					return {
						id: data.id,
						documentNo: data.documentNo,
						documentDate: data.documentDate,
						documentType: data.documentType,
						file: data.file,
						isMandatory: data.isMandatory,
					} as ReferralDocumentItem;
				}) ?? [],
			input: undefined,
		} as ReferralDocumentForm;
	};

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form.Provider>
			<FleetTitle>Data Referral JF</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={getInitialValues()}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['items'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue, resetFields }) => {
						const values = getFieldsValue() as ReferralDocumentForm;

						return (
							<Fragment>
								{!valid && (
									<Alert
										message="Mohon lengkapi seluruh dokumen referral JF yang wajib (*)"
										type="warning"
										showIcon
										className="custom-alert"
										style={{ marginBottom: 20 }}
									/>
								)}
								<Table
									bordered
									dataSource={
										values?.items?.map((item, key) => {
											return { ...item, key };
										}) ?? []
									}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											width: 1,
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<a
																onClick={(e) => {
																	e.preventDefault();
																	if (!record.file?.id) return;
																	openPreviewModal(record.file as FleetFile);
																}}
																style={
																	record.file?.url
																		? { display: 'block' }
																		: {
																				pointerEvents: 'none',
																				color: '#bababa',
																				display: 'block',
																			}
																}
															>
																<FileSearchOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setActiveIndexModal(index);
																}}
															>
																<EditOutlined />
															</a>
														</div>
													</Row>
												);
											},
										},
										{
											title: 'Jenis Dokumen',
											render: (_, record) => {
												return (
													<>
														{record.documentType}
														{record.isMandatory && (
															<span style={{ color: 'red' }}>*</span>
														)}
													</>
												);
											},
										},
										{
											title: 'No Dokumen',
											render: (_, record) => record.documentNo ?? '-',
										},
										{
											title: 'Tanggal Dokumen',
											render: (_, record) =>
												record.documentDate
													? dayjs(record.documentDate).format('YYYY-MM-DD')
													: '-',
										},
									]}
									pagination={false}
								/>

								<FleetModal
									open={typeof activeIndexModal === 'number'}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									okButtonProps={{
										disabled: !values.input?.file?.id,
									}}
									onOk={async () => {
										const isValidForm = await form.validateFields([
											fieldName(['input']),
										]);
										if (isValidForm) {
											form?.submit();
										}
									}}
									onCancel={() => {
										setActiveIndexModal(undefined);
										resetFields([fieldName('input')]);
									}}
								>
									<FleetTitle>Edit Data Legalitas</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Jenis Dokumen"
												name={fieldName(['input', 'documentType'])}
												labelCol={{ span: 8 }}
											>
												<Input.TextArea
													rows={5}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="No. Dokumen"
												name={fieldName(['input', 'documentNo'])}
												labelCol={{ span: 8 }}
												rules={
													form?.getFieldValue(
														fieldName(['input', 'isMandatory']),
													)
														? [rule.required()]
														: []
												}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Tanggal Dokumen"
												name={fieldName(['input', 'documentDate'])}
												labelCol={{ span: 8 }}
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).format('YYYY-MM-DD')
												}
											>
												<DatePicker
													disabledDate={(now) =>
														now > dayjs().subtract(1, 'day')
													}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'file'])}
												noStyle
											/>
											<Form.Item
												label="Upload Dokumen"
												rules={[rule.required()]}
												validateStatus={
													values.input?.file?.id ? 'success' : 'error'
												}
												help={values.input?.file?.id ? '' : 'Wajib diisi'}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													onRemove={() => {
														setFieldValue(fieldName(['input', 'file']), null);
													}}
													fileList={
														values.input?.file
															? [
																	{
																		name: values.input.file.name,
																		uid: values.input.file.id,
																		url: values.input.file.url,
																	} as UploadFile,
																]
															: []
													}
													beforeUpload={async (file) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/referral-documents`;

														try {
															const url = await uploadMutation.mutateAsync({
																file,
																fileid,
																filepath,
															});

															setFieldValue(fieldName(['input', 'file']), {
																format: '',
																id: fileid,
																name: file.name,
																path: filepath,
																url,
															} as ReferralDocumentItem['file']);
														} catch {
															setFieldValue(fieldName(['input', 'file']), null);
														}

														return false;
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
									</Row>
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMPraSurveyInputReferralDocument;
